/**
 * CSV Export Utility
 *
 * Utilities for exporting data to CSV format
 */

import type { ExpenditureRequest } from '../components/request-list';

/**
 * Convert array of objects to CSV string
 */
export function arrayToCSV<T extends Record<string, any>>(
  data: T[],
  columns?: Array<{ key: keyof T; label: string }>
): string {
  if (data.length === 0) return '';

  // Use provided columns or generate from first object
  const firstItem = data[0] as T;
  const cols = columns || Object.keys(firstItem).map(key => ({ key, label: key }));

  // Create header row
  const header = cols.map(col => escapeCSVValue(col.label)).join(',');

  // Create data rows
  const rows = data.map(item =>
    cols.map(col => escapeCSVValue(formatValue(item[col.key]))).join(',')
  );

  return [header, ...rows].join('\n');
}

/**
 * Escape special characters for CSV
 */
function escapeCSVValue(value: string): string {
  // If value contains comma, newline, or quote, wrap in quotes
  if (value.includes(',') || value.includes('\n') || value.includes('"')) {
    // Escape quotes by doubling them
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Format value for CSV output
 */
function formatValue(value: any): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Download CSV file
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
}

/**
 * Format currency amount
 */
function formatCurrency(amount: number | null, currency: string | null): string {
  if (amount === null) return '';
  const cur = currency || 'CHF';
  return `${amount.toFixed(2)} ${cur}`;
}

/**
 * Format date for CSV
 */
function formatDate(timestamp: string | null): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-CH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Export expenditure requests to CSV
 */
export function exportRequestsToCSV(requests: ExpenditureRequest[], filename?: string): void {

  // Transform data for better CSV output
  const transformedData = requests.map(request => ({
    title: request.title || 'Untitled',
    description: request.description || '',
    amount: formatCurrency(request.amount, request.currency),
    currency: request.currency || 'CHF',
    status: request.status,
    creator_name: request.creator_name || '',
    created_at: formatDate(request.created_at),
    submitted_at: formatDate(request.submitted_at),
  }));

  // Generate CSV
  const csvContent = arrayToCSV(transformedData);

  // Generate filename with timestamp
  const defaultFilename = `expenditure_requests_${new Date().toISOString().slice(0, 10)}.csv`;

  // Download the file
  downloadCSV(csvContent, filename || defaultFilename);
}